import { useState } from "react";
import { Modal, Tabs, Tab, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  GLOSSARY_TAB_KEYS,
  TAB_SECTION_KEYS,
  GLOSSARY_DATA_BY_TAB,
} from "./data";

const GlossaryModal = ({ isVisible, handleGlossaryToggle, lessonNum }) => {
  const [currentTab, setCurrentTab] = useState(GLOSSARY_TAB_KEYS.BLOCKS);
  const [selectedItemInfo, setSelectedItemInfo] = useState(null);
  const currentTabOnItemClick =
    currentTab === GLOSSARY_TAB_KEYS.BLOCKS
      ? (info) => void setSelectedItemInfo(info)
      : null;

  const closeModal = () => {
    handleGlossaryToggle();
    setSelectedItemInfo(null);
    setCurrentTab(GLOSSARY_TAB_KEYS.BLOCKS);
  };

  return (
    <Modal
      open={isVisible}
      onClose={closeModal}
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
      disablePortal
    >
      <div className="glossary-modal-container">
        <div className="glossary-modal-header">
          {selectedItemInfo && (
            <IconButton
              onClick={() => void setSelectedItemInfo(null)}
              sx={{
                position: "absolute",
                top: "15px",
                left: "40px",
                zIndex: 1,
                color: "white",
                padding: 0,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <p>{selectedItemInfo ? "State Block" : "Glossary"}</p>
          <IconButton
            onClick={closeModal}
            sx={{
              position: "absolute",
              top: "15px",
              right: "40px",
              zIndex: 1,
              color: "white",
              padding: 0,
            }}
          >
            <ClearIcon />
          </IconButton>
        </div>
        <div className="glossary-content-container">
          {selectedItemInfo ? (
            <ObjectInfoContent info={selectedItemInfo} />
          ) : (
            <GlossaryMainContent
              currentTab={currentTab}
              lessonNum={lessonNum}
              onTabChange={(_, newValue) => {
                setSelectedItemInfo(null);
                setCurrentTab(newValue);
              }}
              onItemClick={currentTabOnItemClick}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

const GlossaryMainContent = ({
  currentTab,
  lessonNum,
  onTabChange,
  onItemClick,
}) => {
  const currentTabData = GLOSSARY_DATA_BY_TAB[currentTab];
  const gridComponentProps = {
    lessonNum,
    onItemClick,
    processData: currentTabData.processData,
  };
  return (
    <>
      <Tabs
        centered
        TabIndicatorProps={{
          style: {
            backgroundColor: "#326941",
            width: `${currentTabData.tabIndicatorWidth}`,
            marginBottom: "1px",
            height: "3px",
            borderRadius: "3px 3px 0px 0px",
            transform: `translate(calc(134px - ${currentTabData.tabIndicatorWidth}/2), 0%)`,
          },
        }}
        variant="fullWidth"
        value={currentTab}
        onChange={onTabChange}
        sx={{
          flex: "0 1 48px",
          boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.10)",
        }}
      >
        {Object.keys(GLOSSARY_DATA_BY_TAB).map(
          (tabKey) =>
            GLOSSARY_DATA_BY_TAB[tabKey] && (
              <Tab
                className="glossary-tab-"
                key={tabKey}
                value={tabKey}
                label={GLOSSARY_DATA_BY_TAB[tabKey].title}
                sx={{
                  paddingTop: 0,
                  textTransform: "none",
                  backgroundColor: "#d3e8d2",
                  padding: 0,
                  font: "normal 500 16px Roboto",
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  color: "#326941 !important",
                  opacity: 1,
                  "&:hover": {
                    backgroundColor: "#bfccc6",
                  },
                }}
              />
            )
        )}
      </Tabs>
      <div className="glossary-grid-outer-container">
        <div className="glossary-grid-inner-container">
          {TAB_SECTION_KEYS[currentTab] ? (
            Object.keys(TAB_SECTION_KEYS[currentTab]).map((sectionKey) => {
              const { title, data } = currentTabData[sectionKey];
              return (
                <div className="glossary-section-container" key={sectionKey}>
                  <span className="glossary-section-title">{title}</span>
                  <div className="glossary-section-divider" />
                  <PopulatedGlossaryGrid data={data} {...gridComponentProps} />
                </div>
              );
            })
          ) : (
            <PopulatedGlossaryGrid
              data={currentTabData.data}
              {...gridComponentProps}
            />
          )}
        </div>
      </div>
    </>
  );
};

const ObjectInfoContent = ({
  info: { gif, definition, thingsToConsider, label },
}) => (
  <div className="glossary-object-info-content-outer-container">
    <div className="glossary-object-info-content-inner-container">
      <div className="glossary-object-info-gif-outer-container">
        <div
          className="glossary-object-info-gif-inner-container"
          style={{ background: `url(${gif}) lightgray 50% / cover no-repeat` }}
        />
      </div>
      <div className="glossary-object-info-bottom-container">
        <div className="glossary-object-info-label">{label}</div>
        <div className="glossary-object-info-description-divider" />
        <div className="glossary-object-info-section-container">
          <div className="glossary-object-info-section">
            <div className="glossary-object-info-section-label">
              Definition:
            </div>
            <div className="glossary-object-info-section-text">
              {definition}
            </div>
          </div>
          <div className="glossary-object-info-section">
            <div className="glossary-object-info-section-label">
              Key Details:
            </div>
            <div className="glossary-object-info-section-text">
              {thingsToConsider.map((detail, i) => (
                <div key={`key-detail-${i}`}>
                  <span>{detail}</span>
                  <br />
                  <br />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const PopulatedGlossaryGrid = ({
  data,
  lessonNum,
  onItemClick,
  processData = (data) => data,
}) => (
  <div className="glossary-grid-component">
    {processData(data, { lessonNum }).map(({ key, label, img, info }) => (
      <GlossaryGridImageItem
        key={key}
        imgSrc={img}
        label={label}
        onClick={onItemClick ? () => onItemClick({ ...info, label }) : null}
      />
    ))}
  </div>
);

const GlossaryGridImageItem = ({ imgSrc, label, onClick }) => (
  <div className="glossary-grid-item-container">
    <div
      className={`glossary-item-img-container ${
        !!onClick ? "clickable-glossary-item" : ""
      }`}
      onClick={onClick}
    >
      <img src={imgSrc} alt={imgSrc} />
    </div>
    <span className="glossary-item-label">{label}</span>
  </div>
);

export default GlossaryModal;
